import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Col, Row } from "react-bootstrap";
import CloseIcon from "@mui/icons-material/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import InputTextField from "../../../component/input/inputTextField";
import ButtonCustom from "../../../component/button/buttonCustom";
import { colors } from "../../../constants/colors";

/** ข้อจำกัดความยาวตาม announce-links-api.md ข้อ 5 */
export const LINK_NAME_MAX = 255;
export const LINK_URL_MAX = 2048;

export type AnnounceLinkItem = {
  rowId: string;
  linkName: string;
  url: string;
};

let rowSeq = 0;
export const createLinkRow = (): AnnounceLinkItem => {
  rowSeq = rowSeq + 1;
  return { rowId: `link-${Date.now()}-${rowSeq}`, linkName: "", url: "" };
};

/**
 * map `announceLinks` จาก GET /announce/:id เข้าฟอร์ม
 * ไม่ใช้ announceLinkId เป็น key เพราะ PATCH เป็น replace-all — id เปลี่ยนใหม่ทุกครั้งที่บันทึก
 */
export const mapAnnounceLinks = (announceLinks: any): AnnounceLinkItem[] =>
  (Array.isArray(announceLinks) ? announceLinks : []).map((e: any) => ({
    ...createLinkRow(),
    linkName: e?.linkName || "",
    url: e?.url || "",
  }));

/** แปลง state ฟอร์มเป็น payload `links` — row ที่ url ว่างถูกตัดออก, [] = ลบลิงก์เดิมทั้งหมด */
export const toLinksPayload = (links: AnnounceLinkItem[]) =>
  (links || [])
    .filter((e: AnnounceLinkItem) => (e?.url || "").trim())
    .map((e: AnnounceLinkItem) => ({
      linkName: (e.linkName || "").trim() || null,
      url: e.url.trim(),
    }));

const isValidUrl = (url: string) => {
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/** validate ก่อน submit — backend ไม่ตรวจรูปแบบ URL ให้ ({} = ผ่าน) */
export const validateLinks = (links: AnnounceLinkItem[]) => {
  const errors: { [rowId: string]: string } = {};
  (links || []).forEach((e: AnnounceLinkItem) => {
    const url = (e?.url || "").trim();
    const linkName = (e?.linkName || "").trim();
    // row ว่างทั้งคู่ = กดเพิ่มแล้วไม่กรอก ปล่อยผ่านแล้วตัดทิ้งตอนส่ง
    if (!url && !linkName) return;
    if (!url) errors[e.rowId] = "กรุณาระบุ URL ของลิงก์";
    else if (url.length > LINK_URL_MAX)
      errors[e.rowId] = `URL ยาวเกิน ${LINK_URL_MAX} ตัวอักษร`;
    else if (!isValidUrl(url))
      errors[e.rowId] = "รูปแบบ URL ไม่ถูกต้อง ต้องเริ่มด้วย http:// หรือ https://";
    else if (linkName.length > LINK_NAME_MAX)
      errors[e.rowId] = `ชื่อลิงก์ยาวเกิน ${LINK_NAME_MAX} ตัวอักษร`;
  });
  return errors;
};

type AnnounceLinksProps = {
  links: AnnounceLinkItem[];
  onChange: (links: AnnounceLinkItem[]) => void;
  errors?: { [rowId: string]: string };
  disabled?: boolean;
};

/** ฟอร์มแนบลิงก์ (เพิ่ม/ลบ/แก้ไขใน state ฝั่ง front — ยังไม่ยิง API) */
export default function AnnounceLinks(props: AnnounceLinksProps) {
  const { links, onChange, errors, disabled } = props;
  const linkErrors = errors || {};

  const handleField = (
    rowId: string,
    field: "linkName" | "url",
    value: string
  ) => {
    onChange(
      (links || []).map((e: AnnounceLinkItem) =>
        e.rowId === rowId ? { ...e, [field]: value } : e
      )
    );
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.black_12}`,
        borderRadius: "8px",
        backgroundColor: colors.white,
        padding: "16px",
      }}
    >
      {links?.map((item: AnnounceLinkItem) => (
        <Row key={item.rowId}>
          <Col lg={5} md={12} xs={12} className="my-1">
            <InputTextField
              value={item.linkName}
              disabled={disabled}
              placeholder="ชื่อลิงก์ เช่น แบบฟอร์มยื่นคำขอ"
              inputProps={{ maxLength: LINK_NAME_MAX }}
              onchange={(e) =>
                handleField(item.rowId, "linkName", e.target.value)
              }
            />
          </Col>
          <Col lg={6} md={11} xs={10} className="my-1">
            <InputTextField
              value={item.url}
              disabled={disabled}
              placeholder="https://"
              inputProps={{ maxLength: LINK_URL_MAX }}
              helperText={linkErrors[item.rowId] || ""}
              onchange={(e) => handleField(item.rowId, "url", e.target.value)}
            />
          </Col>
          <Col
            lg={1}
            md={1}
            xs={2}
            className="my-1 d-flex align-items-start justify-content-center"
          >
            <Tooltip title="ลบลิงก์">
              <IconButton
                disabled={disabled}
                onClick={() =>
                  onChange(
                    (links || []).filter(
                      (e: AnnounceLinkItem) => e.rowId !== item.rowId
                    )
                  )
                }
                sx={{ height: 43 }}
              >
                <CloseIcon sx={{ color: colors.red, fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Col>
        </Row>
      ))}

      <ButtonCustom
        variant="outlined"
        disabled={disabled}
        onClick={() => onChange([...(links || []), createLinkRow()])}
        startIcon={<FontAwesomeIcon icon={faLink} style={{ fontSize: 14 }} />}
        textButton="เพิ่มลิงก์"
        style={{ margin: 0, width: "fit-content" }}
        btnStyle={{ height: 40, padding: "0 16px", marginTop: "8px" }}
      />
    </Box>
  );
}

/** แสดงลิงก์แนบแบบอ่านอย่างเดียว (หน้ารายละเอียด) */
export function AnnounceLinkList({
  announceLinks,
  heading,
}: {
  announceLinks: any;
  heading?: string;
}) {
  const list = (Array.isArray(announceLinks) ? announceLinks : []).filter(
    (e: any) => e?.url
  );
  if (!list.length) return null;

  return (
    <Box sx={{ marginTop: "28px" }}>
      <Typography
        sx={{
          fontFamily: "Kanit",
          fontSize: "17px",
          fontWeight: 600,
          color: colors.textPrimary,
          marginBottom: "12px",
        }}
      >
        {heading || "ลิงก์ที่เกี่ยวข้อง"}
      </Typography>
      {list.map((value: any, index: number) => (
        <Box
          key={value?.announceLinkId || index}
          sx={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}
        >
          <FontAwesomeIcon
            icon={faLink}
            style={{ color: colors.themeMainColor, fontSize: 15, marginTop: 5 }}
          />
          <a
            href={value.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "Kanit",
              fontSize: "16px",
              color: colors.themeMainColor,
              wordBreak: "break-all",
            }}
          >
            {value.linkName || value.url}
          </a>
        </Box>
      ))}
    </Box>
  );
}
